<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(defineProps<{
  variant?: 'no-access' | 'offline'
}>(), {
  variant: 'no-access'
});

const { t } = useI18n();

const title = computed(() =>
    props.variant === 'offline' ? t('miniMap.offlineTitle') : t('miniMap.noAccessTitle')
);
const caption = computed(() =>
    props.variant === 'offline' ? t('miniMap.offlineCaption') : t('miniMap.noAccessCaption')
);
</script>

<template>
  <div class="holo" role="img" :aria-label="`${title}. ${caption}`">
    <svg class="holo--svg" viewBox="0 0 200 200" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
      <defs>
        <clipPath id="holoClip">
          <circle cx="100" cy="100" r="76"/>
        </clipPath>
        <radialGradient id="holoCore" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stop-color="rgba(22, 234, 255, 0.16)"/>
          <stop offset="62%" stop-color="rgba(22, 234, 255, 0.06)"/>
          <stop offset="100%" stop-color="rgba(0, 0, 0, 0)"/>
        </radialGradient>
        <linearGradient id="holoSweep" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stop-color="rgba(22, 234, 255, 0)"/>
          <stop offset="50%" stop-color="rgba(22, 234, 255, 0.4)"/>
          <stop offset="100%" stop-color="rgba(22, 234, 255, 0)"/>
        </linearGradient>
      </defs>

      <g class="holo--outer">
        <circle class="holo--outer-ring" cx="100" cy="100" r="94"/>
        <circle class="holo--outer-ring holo--outer-ring-b" cx="100" cy="100" r="88"/>
      </g>

      <circle cx="100" cy="100" r="76" fill="url(#holoCore)"/>

      <g clip-path="url(#holoClip)" class="holo--grid">
        <line v-for="n in 11" :key="'v' + n" :x1="14 * n + 22" :x2="14 * n + 22" y1="0" y2="200"/>
        <line v-for="n in 11" :key="'h' + n" x1="0" x2="200" :y1="14 * n + 22" :y2="14 * n + 22"/>
      </g>

      <g clip-path="url(#holoClip)">
        <rect class="holo--scan" x="0" y="-40" width="200" height="40" fill="url(#holoSweep)"/>
      </g>

      <circle class="holo--ring" cx="100" cy="100" r="76"/>
      <circle class="holo--ring-inner" cx="100" cy="100" r="48"/>
      <circle class="holo--pip" cx="100" cy="100" r="3"/>

      <g class="holo--ticks">
        <line x1="100" y1="14" x2="100" y2="34"/>
        <line x1="100" y1="166" x2="100" y2="186"/>
        <line x1="14" y1="100" x2="34" y2="100"/>
        <line x1="166" y1="100" x2="186" y2="100"/>
      </g>
    </svg>

    <div class="holo--label">
      <span class="holo--title">{{ title }}</span>
      <span class="holo--caption">{{ caption }}</span>
    </div>
  </div>
</template>

<style scoped>
.holo {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.holo--svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.holo--grid line {
  stroke: rgba(22, 234, 255, 0.22);
  stroke-width: 1;
}

.holo--outer-ring {
  fill: none;
  stroke: rgba(22, 234, 255, 0.45);
  stroke-width: 1;
  stroke-dasharray: 26 14 4 14;
}

.holo--outer-ring-b {
  stroke: rgba(22, 234, 255, 0.24);
  stroke-dasharray: 3 11;
}

.holo--ring {
  fill: rgba(2, 12, 18, 0.4);
  stroke: rgba(22, 234, 255, 0.85);
  stroke-width: 1.4;
  filter: drop-shadow(0 0 3px rgba(22, 234, 255, 0.55));
  animation: holo-breathe 4.5s ease-in-out infinite;
  transform-origin: 100px 100px;
}

.holo--ring-inner {
  fill: none;
  stroke: rgba(22, 234, 255, 0.22);
  stroke-width: 0.8;
  stroke-dasharray: 5 9;
}

.holo--pip {
  fill: rgba(22, 234, 255, 0.85);
  animation: holo-breathe 2.6s ease-in-out infinite;
  transform-origin: 100px 100px;
}

.holo--ticks line {
  stroke: rgba(22, 234, 255, 0.9);
  stroke-width: 2.4;
  stroke-linecap: round;
  filter: drop-shadow(0 0 2px rgba(22, 234, 255, 0.6));
}

.holo--scan {
  animation: holo-scan 6s linear infinite;
}

.holo--label {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  max-width: 66%;
  text-align: center;
  color: rgba(22, 234, 255, 0.95);
  text-shadow: 0 0 0.45rem rgba(22, 234, 255, 0.8);
}

.holo--title {
  font-size: 0.72rem;
  font-weight: bold;
  letter-spacing: 0.1em;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.holo--caption {
  font-size: 0.56rem;
  line-height: 1.3;
  color: rgba(22, 234, 255, 0.7);
  text-shadow: none;
  overflow-wrap: anywhere;
}

@keyframes holo-scan {
  from { transform: translateY(0); }
  to { transform: translateY(240px); }
}

@keyframes holo-breathe {
  0%, 100% { opacity: 0.6; }
  50% { opacity: 1; }
}

@media (prefers-reduced-motion: reduce) {
  .holo--scan {
    animation: none;
    transform: translateY(120px);
    opacity: 0.5;
  }

  .holo--ring,
  .holo--pip {
    animation: none;
    opacity: 0.9;
  }
}
</style>
